import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import headerLogo from "../assets/images/header_logo_color.png";
import {
  DASH,
  EQUIPMENT_PASSPORT_TYPES,
  displayValue,
  formatDate,
  getEquipmentTitle,
  getFieldRows,
} from "../utils/equipmentPassportConfig";

const PUBLIC_HIDDEN_FIELD_KEYS = new Set([
  "allowed_nominal_current_a",
  "max_load_current_a",
]);

const TEMPORARY_RESPONSIBLE_FIELD = {
  key: "responsible_person",
  label: "Жавобгар шахс",
  value: "Озодлик ПС бошлиғи Эшов Хусен",
};

function PublicPassportState({ title, text, tone = "default" }) {
  return (
    <div className="public-passport-page">
      <header className="public-passport-header">
        <div className="public-passport-header__inner">
          <div className="public-passport-brand">
            <img src={headerLogo} alt="Ўзбекистон миллий электр тармоқлари" />
            <div>
              <strong>Ўзбекистон миллий электр тармоқлари</strong>
              <span>Ускуналарнинг очиқ паспортлари</span>
            </div>
          </div>
        </div>
      </header>

      <main className="public-passport-state-wrap">
        <section className={`public-passport-state public-passport-state--${tone}`}>
          <span className="public-passport-state__mark" aria-hidden="true">
            {tone === "error" ? "!" : "…"}
          </span>
          <h1>{title}</h1>
          {text && <p>{text}</p>}
        </section>
      </main>
    </div>
  );
}

function PublicEquipmentPassport() {
  const { slug, publicId } = useParams();
  const equipmentPublicId = slug || publicId || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPassport() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/public/equipment/${encodeURIComponent(equipmentPublicId)}`,
          { cache: "no-store" }
        );
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          const requestError = new Error(
            payload.message || "Ускуна паспортини юклаб бўлмади"
          );
          requestError.status = response.status;
          throw requestError;
        }

        if (!cancelled) setData(payload);
      } catch (requestError) {
        if (!cancelled) {
          setError({
            status: requestError.status,
            message: requestError.message,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPassport();

    return () => {
      cancelled = true;
    };
  }, [equipmentPublicId]);

  const view = useMemo(() => {
    if (!data) return null;

    const cfg = EQUIPMENT_PASSPORT_TYPES[data.type] || {
      label: data.type_label || "Ускуна",
      titleKeys: ["name"],
      fields: [],
    };
    const equipment = data.equipment || {};

    return {
      cfg,
      equipment,
      title: getEquipmentTitle(equipment, cfg),
      fields: [
        ...getFieldRows(equipment, cfg).filter(
          (field) => !PUBLIC_HIDDEN_FIELD_KEYS.has(field.key)
        ),
        TEMPORARY_RESPONSIBLE_FIELD,
      ],
      substation: data.substation || null,
      lastMaintenance: data.last_maintenance || null,
      privatePassportPath: data.links?.private_passport || null,
    };
  }, [data]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = view
      ? `${view.title} — очиқ паспорт`
      : "Ускунанинг паспорти";

    return () => {
      document.title = previousTitle;
    };
  }, [view]);

  if (loading) {
    return (
      <PublicPassportState
        title="Паспорт юкланмоқда"
        text="Ускуна маълумотлари тайёрланмоқда."
      />
    );
  }

  if (error || !view) {
    const notFound = error?.status === 404;

    return (
      <PublicPassportState
        title={notFound ? "Паспорт топилмади" : "Паспортни очиб бўлмади"}
        text={
          notFound
            ? "QR-коддаги ҳаволани текширинг ёки масъул ходимга мурожаат қилинг."
            : "Маълумотларни юклашда хатолик юз берди. Кейинроқ қайта уриниб кўринг."
        }
        tone="error"
      />
    );
  }

  const repairDate = view.lastMaintenance?.event_date;
  const repairTopic = view.lastMaintenance?.topic;
  const passportNumber = view.equipment?.slug || equipmentPublicId;
  const privatePassportHref = view.privatePassportPath
    ? localStorage.getItem("token")
      ? view.privatePassportPath
      : `/login?returnUrl=${encodeURIComponent(view.privatePassportPath)}`
    : null;

  return (
    <div className="public-passport-page">
      <header className="public-passport-header">
        <div className="public-passport-header__inner">
          <div className="public-passport-brand">
            <img src={headerLogo} alt="Ўзбекистон миллий электр тармоқлари" />
            <div>
              <strong>Ўзбекистон миллий электр тармоқлари</strong>
              <span>Ускуналарнинг очиқ паспортлари</span>
            </div>
          </div>

          <span className="public-passport-status">
            <span className="public-passport-status__icon" aria-hidden="true" />
            Очиқ маълумот
          </span>
        </div>
      </header>

      <main className="public-passport-shell">
        <section className="public-passport-hero">
          <div className="public-passport-hero__content">
            <span className="public-passport-eyebrow">Ускунанинг паспорти</span>
            <h1>{view.title}</h1>
            <p>
              Техник кўрсаткичлар, жойлашув ва сўнгги таъмир ҳақидаги
              тасдиқланган маълумотлар.
            </p>
          </div>

          <div className="public-passport-type-card">
            <span>Ускуна тури</span>
            <strong>{view.cfg.label}</strong>
            {/* <small>Паспорт рақами · {String(passportNumber).slice(0, 8).toUpperCase()}</small> */}
          </div>
        </section>

        <section className="public-passport-location" aria-label="Жойлашув">
          <article>
            <span>Подстанция</span>
            <strong>{view.substation?.name || DASH}</strong>
          </article>
          <article>
            <span>Филиал</span>
            <strong>{view.substation?.city?.name || DASH}</strong>
          </article>
        </section>

        <div className="public-passport-layout">
          <section className="public-passport-card public-passport-card--specs">
            <div className="public-passport-card__head">
              <span className="public-passport-section-index">01</span>
              <div>
                <h2>Техник маълумотлар</h2>
                <p>Ускунанинг асосий паспорт кўрсаткичлари</p>
              </div>
            </div>

            <dl className="public-passport-fields">
              {view.fields.map((field) => (
                <div key={field.key}>
                  <dt>{field.label}</dt>
                  <dd>{displayValue(field.value)}</dd>
                </div>
              ))}
            </dl>
          </section>

          <aside className="public-passport-aside">
            <section className="public-passport-card public-passport-repair">
              <div className="public-passport-card__head">
                <span className="public-passport-section-index public-passport-section-index--repair">
                  02
                </span>
                <div>
                  <h2>Сўнгги таъмир</h2>
                  <p>Охирги қайд этилган маълумот</p>
                </div>
              </div>

              {view.lastMaintenance ? (
                <div className="public-passport-repair__body">
                  <span>Таъмир мавзуси</span>
                  <strong>{repairTopic || DASH}</strong>
                  <div>
                    <span>Таъмир санаси</span>
                    <b>{formatDate(repairDate)}</b>
                  </div>
                </div>
              ) : (
                <div className="public-passport-empty">
                  Таъмир ҳақида маълумот мавжуд эмас.
                </div>
              )}
            </section>

            {/* <section className="public-passport-note">
              <span aria-hidden="true">i</span>
              <p>
                Ушбу саҳифада фақат очиқ паспорт маълумотлари кўрсатилган.
              </p>
            </section> */}
          </aside>
        </div>
      </main>

      <footer className="public-passport-footer">
        <div>
          <div className="public-passport-footer__copy">
            <span>Ходимлар учун</span>
            <strong>Ускунанинг тўлиқ паспорти</strong>
          </div>

          {privatePassportHref && (
            <Link className="public-passport-private-link" to={privatePassportHref}>
              Тўлиқ паспортни очиш
              <span aria-hidden="true">→</span>
            </Link>
          )}
        </div>
      </footer>
    </div>
  );
}

export default PublicEquipmentPassport;
